import React, { useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  Dialog,
  Grid,
  IconButton,
  Divider,
  MenuItem,
  Collapse,
  Radio,
  RadioGroup,
  FormGroup,
  makeStyles,
} from '@material-ui/core';
import { toastr } from 'react-redux-toastr';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import {
  assignCompanionsToRooms,
  countSelectedSports,
  countSelectedSarau,
  findHotelInActivity,
  formatPhone,
  formatExtraRoomsBillingLabel,
  getSportFieldKeys,
  getSarauFieldKeys,
  getCompanionPaymentStatus,
  isMinorByBirthYear,
  isPartnerPerCategory,
  isValidBirthday,
  resolveActiveRoomRules,
} from './config';

const UF_LIST = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const formatMoney = (value) => Number(value || 0).toFixed(2).replace('.', ',');

const formatAdditionalRoomPriceLabel = (roomRules = {}) => {
  const firstTier = Array.isArray(roomRules.additionalRoomPricingTiers)
    ? roomRules.additionalRoomPricingTiers[0]
    : null;
  const roomPrice = Number(firstTier?.room_price ?? roomRules.extraRoomPrice ?? 0);
  const companionPrice = Number(firstTier?.companion_price ?? 0);

  if (roomPrice > 0 && companionPrice > 0) {
    return `R$ ${formatMoney(roomPrice)} + R$ ${formatMoney(
      companionPrice
    )} por pessoa`;
  }
  if (companionPrice > 0) {
    return `R$ ${formatMoney(companionPrice)} por pessoa`;
  }
  if (roomPrice > 0) {
    return `R$ ${formatMoney(roomPrice)}`;
  }
  return null;
};

const isHotelSelectable = (hotel) =>
  !!hotel &&
  hotel.active !== false &&
  (!hotel.isLimited ||
    (typeof hotel.remaining === 'number' && hotel.remaining > 0));

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 4,
    color: '#1a3a5c',
  },
  subtitle: {
    textAlign: 'center',
    color: '#5a7a9a',
    marginBottom: theme.spacing(3),
  },
  aloneCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    transition: 'all 0.2s',
  },
  aloneCardSelected: {
    border: '2px solid #1a3a5c',
    backgroundColor: '#f0f4f8',
  },
  hotelGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  hotelCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 12,
    padding: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#fff',
    position: 'relative',
  },
  hotelCardSelected: {
    border: '2px solid #1a3a5c',
    backgroundColor: '#f0f4f8',
    boxShadow: '0 2px 8px rgba(26,58,92,0.12)',
  },
  hotelCardDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
    backgroundColor: '#fafafa',
  },
  hotelBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 12,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  companionCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    backgroundColor: '#fafafa',
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  btnAdd: {
    backgroundColor: '#1a3a5c',
    color: '#fff',
    fontWeight: 'bold',
    borderRadius: 8,
    textTransform: 'none',
    '&:hover': { backgroundColor: '#15304d' },
  },
  valuesBox: {
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

function formatCPF(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(
    6,
    9
  )}-${digits.slice(9)}`;
}

function formatDate(value) {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

const COMPANION_TERMS = [
  {
    field: 'aptidao_fisica',
    label:
      'Declaro que o acompanhante está apto(a) fisicamente para participar das atividades esportivas.',
    required: true,
  },
  {
    field: 'regras_organizacao',
    label:
      'Estou ciente e concordo com as regras da organização em nome do acompanhante.',
    required: true,
  },
  {
    field: 'uso_imagem',
    label:
      'Autorizo o uso de imagem do acompanhante em materiais institucionais do Sindilegis.',
    required: false,
  },
  {
    field: 'lgpd',
    label:
      'Autorizo o Sindilegis a coletar, armazenar e tratar os dados pessoais do acompanhante exclusivamente para fins do evento, conforme a Lei 13.709/2018.',
    required: true,
  },
];

const EMPTY_COMPANION = {
  name: '',
  cpf: '',
  birthday: '',
  city: '',
  state: '',
  phone: '',
  email: '',
  is_minor: false,
  room_number: 1,
  modalities: {},
  sarau_activities: {},
  terms: {},
};

function CompanionModal({
  open,
  onClose,
  onSave,
  initialData,
  roomRules,
  activityConfig,
}) {
  const minorAgeLimit = roomRules?.minorAgeLimit ?? 17;
  const minorsArePaying = roomRules?.minorsCountTowardsPayment === true;
  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [birthday, setBirthday] = useState('');
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isMinor, setIsMinor] = useState(false);
  const [modalities, setModalities] = useState({});
  const [sarauActivities, setSarauActivities] = useState({});
  const [sportsOpen, setSportsOpen] = useState(false);
  const [sarauOpen, setSarauOpen] = useState(false);
  const [terms, setTerms] = useState({});

  const checkIfMinor = React.useCallback((dateStr) => {
    if (!isValidBirthday(dateStr)) return null;
    return isMinorByBirthYear(dateStr, minorAgeLimit, {
      referenceDate: roomRules?.ageReferenceDate,
      ageValidationMode: roomRules?.ageValidationMode,
    });
  }, [minorAgeLimit, roomRules]);

  React.useEffect(() => {
    if (open) {
      setName(initialData.name || '');
      setCpf(initialData.cpf || '');
      setBirthday(initialData.birthday || '');
      setCity(initialData.city || '');
      setUf(initialData.state || '');
      setEmail(initialData.email || '');
      setPhone(formatPhone(initialData.phone || ''));
      const detectedMinor = checkIfMinor(initialData.birthday || '');
      setIsMinor(
        detectedMinor !== null ? detectedMinor : initialData.is_minor || false
      );
      setModalities(initialData.modalities || {});
      setSarauActivities(initialData.sarau_activities || {});
      setSportsOpen(false);
      setSarauOpen(false);
      setTerms(initialData.terms || {});
    }
  }, [open, initialData, checkIfMinor]);

  const handleBirthdayChange = (value) => {
    const formatted = formatDate(value);
    setBirthday(formatted);
    const minor = checkIfMinor(formatted);
    if (minor !== null) {
      setIsMinor(minor);
    }
  };

  const sports = activityConfig?.sports || [];
  const sarauOptions = activityConfig?.sarau || [];
  const sportLimit = activityConfig?.sportsLimit || 3;
  const sarauLimit = activityConfig?.sarauLimit || 3;

  const handleSportToggle = (sport) => {
    const optOut = sports.find((s) => s.special_type === 'opt_out');
    if (sport.special_type === 'opt_out') {
      const reset = {};
      sports.forEach((s) => {
        reset[s.id] = false;
      });
      setModalities((prev) => ({
        ...prev,
        ...reset,
        [sport.id]: !prev[sport.id],
      }));
    } else {
      setModalities((prev) => {
        const next = { ...prev, [sport.id]: !prev[sport.id] };
        if (optOut) next[optOut.id] = false;
        return next;
      });
    }
  };

  const handleSarauToggle = (item) => {
    const optOut = sarauOptions.find((s) => s.special_type === 'opt_out');
    if (item.special_type === 'opt_out') {
      const reset = {};
      sarauOptions.forEach((s) => {
        reset[s.id] = false;
      });
      setSarauActivities((prev) => ({
        ...prev,
        ...reset,
        [item.id]: !prev[item.id],
      }));
    } else {
      setSarauActivities((prev) => {
        const next = { ...prev, [item.id]: !prev[item.id] };
        if (optOut) next[optOut.id] = false;
        return next;
      });
    }
  };

  const updateModality = (key, value) =>
    setModalities((prev) => ({ ...prev, [key]: value }));
  const updateSarau = (key, value) =>
    setSarauActivities((prev) => ({ ...prev, [key]: value }));

  const availableSports = activityConfig?.sports || [];
  const availableSarau = activityConfig?.sarau || [];
  const hasSportSelection = availableSports.some(
    (sport) => !!modalities[sport.id]
  );
  const hasSarauSelection = availableSarau.some(
    (item) => !!sarauActivities[item.id]
  );

  const handleSave = () => {
    if (!name || !cpf || !birthday || !city || !uf) return;
    if (!isValidBirthday(birthday)) return;
    if (availableSports.length > 0 && !hasSportSelection) {
      toastr.warning(
        'Atenção',
        'Selecione ao menos uma modalidade esportiva para o acompanhante (ou "Não irei participar").'
      );
      return;
    }
    if (availableSarau.length > 0 && !hasSarauSelection) {
      toastr.warning(
        'Atenção',
        'Selecione ao menos uma atividade do Sarau para o acompanhante (ou "Não irei participar").'
      );
      return;
    }
    onSave({
      name,
      cpf,
      birthday,
      city,
      state: uf,
      email,
      phone,
      is_minor: isMinor,
      room_number: initialData.room_number || 1,
      modalities,
      sarau_activities: sarauActivities,
      terms,
    });
  };

  const requiredTermsAccepted = COMPANION_TERMS.filter((t) => t.required).every(
    (t) => !!terms[t.field]
  );
  const canSave =
    name &&
    cpf &&
    birthday &&
    city &&
    uf &&
    isValidBirthday(birthday) &&
    requiredTermsAccepted &&
    (availableSports.length === 0 || hasSportSelection) &&
    (availableSarau.length === 0 || hasSarauSelection);
  const autoDetectedMinor = checkIfMinor(birthday);
  const companionPaymentStatus = getCompanionPaymentStatus(
    { birthday, is_minor: isMinor },
    roomRules || {}
  );
  const birthdayLooksComplete = `${birthday}`.replace(/\D/g, '').length === 8;
  const birthdayHasError = birthdayLooksComplete && !isValidBirthday(birthday);

  const labelStyle = {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
    marginBottom: 6,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      PaperProps={{
        style: { borderRadius: 16, maxHeight: '90vh', overflowY: 'auto' },
      }}
    >
      <Box style={{ padding: '28px 32px 0 32px' }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography
              style={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a3a5c' }}
            >
              Adicionar Acompanhante
            </Typography>
            <Typography
              variant="body2"
              style={{ color: '#5a9a7a', marginTop: 2 }}
            >
              Preencha os dados do acompanhante
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            style={{ marginTop: -4, marginRight: -8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box style={{ padding: '20px 32px 8px 32px' }}>
        <Typography variant="body2" style={labelStyle}>
          Nome Completo *
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          style={{ marginBottom: 16 }}
        />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" style={labelStyle}>
              CPF *
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={cpf}
              onChange={(e) => {
                var v = e && e.target ? e.target.value : '';
                setCpf(formatCPF(v));
              }}
              placeholder="000.000.000-00"
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" style={labelStyle}>
              Data de Nascimento *
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={birthday}
              error={birthdayHasError}
              helperText={birthdayHasError ? 'Informe uma data válida.' : ''}
              inputProps={{ maxLength: 10 }}
              onChange={(e) => {
                var v = e && e.target ? e.target.value : '';
                handleBirthdayChange(v);
              }}
              placeholder="DD/MM/AAAA"
            />
          </Grid>
        </Grid>

        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" style={labelStyle}>
                Cidade *
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" style={labelStyle}>
                UF *
              </Typography>
              <TextField
                fullWidth
                select
                variant="outlined"
                size="small"
                value={uf}
                onChange={(e) => {
                  var v = e && e.target ? e.target.value : '';
                  setUf(v);
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: { maxHeight: 220, borderRadius: 8, marginTop: 4 },
                    },
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    getContentAnchorEl: null,
                  },
                }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {UF_LIST.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>

        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" style={labelStyle}>
                E-mail
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" style={labelStyle}>
                Celular
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={phone}
                onChange={(e) => {
                  var v = e && e.target ? e.target.value : '';
                  setPhone(formatPhone(v));
                }}
                placeholder="(00) 9 0000-0000"
              />
            </Grid>
          </Grid>
        </Box>

        <Box mt={2} mb={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isMinor}
                onChange={(e) => {
                  if (autoDetectedMinor === null) {
                    setIsMinor(e.target.checked);
                  }
                }}
                color="primary"
                disabled={autoDetectedMinor !== null}
              />
            }
            label={
              <Typography
                variant="body2"
                style={{
                  color: autoDetectedMinor !== null ? '#666' : undefined,
                }}
              >
                {autoDetectedMinor !== null
                  ? companionPaymentStatus.label
                    ? companionPaymentStatus.label.replace(/[()]/g, '')
                    : 'Maior de idade detectado automaticamente'
                  : `Este acompanhante é menor de idade (${
                      minorsArePaying ? 'pagante' : 'não pagante'
                    })`}
              </Typography>
            }
          />
        </Box>
      </Box>

      {/* Modalidades Esportivas do acompanhante */}
      {sports.length > 0 && (
        <Box style={{ padding: '0 32px 8px 32px' }}>
          <Divider style={{ marginBottom: 4 }} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            onClick={() => setSportsOpen(!sportsOpen)}
            style={{ cursor: 'pointer', padding: '8px 0' }}
          >
            <Box>
              <Typography
                style={{ fontWeight: 700, fontSize: '1rem', color: '#1a3a5c' }}
              >
                Modalidades Esportivas
              </Typography>
              <Typography
                variant="body2"
                style={{ color: '#666', fontSize: '0.8rem' }}
              >
                Selecione até {sportLimit} modalidades
              </Typography>
            </Box>
            <ExpandMoreIcon
              style={{
                color: '#1a3a5c',
                transform: sportsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
          <Collapse in={sportsOpen}>
            {sports.map((sport) => {
              const keys = getSportFieldKeys(sport);
              const checked = !!modalities[sport.id];
              const sportCount = countSelectedSports(modalities, sports);
              const disabled =
                !checked &&
                sport.special_type !== 'opt_out' &&
                sportCount >= sportLimit;
              return (
                <Box key={sport.id} style={{ marginBottom: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() => handleSportToggle(sport)}
                        disabled={disabled}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        style={{ fontWeight: checked ? 600 : 400 }}
                      >
                        {sport.name}
                      </Typography>
                    }
                  />
                  <Collapse in={checked}>
                    <Box ml={4} mb={1}>
                      {sport.shirt?.enabled && (
                        <Box mb={1}>
                          <Typography
                            variant="caption"
                            style={{ fontWeight: 600 }}
                          >
                            Tamanho da Camisa{sport.shirt.required ? ' *' : ''}
                          </Typography>
                          <Box
                            display="flex"
                            style={{ gap: 4, marginTop: 4, flexWrap: 'wrap' }}
                          >
                            {sport.shirt.sizes.map((size) => (
                              <Box
                                key={size}
                                onClick={() => updateModality(keys.shirt, size)}
                                style={{
                                  padding: '3px 10px',
                                  borderRadius: 6,
                                  border:
                                    modalities[keys.shirt] === size
                                      ? '2px solid #1a3a5c'
                                      : '1px solid #ccc',
                                  backgroundColor:
                                    modalities[keys.shirt] === size
                                      ? '#1a3a5c'
                                      : '#fff',
                                  color:
                                    modalities[keys.shirt] === size
                                      ? '#fff'
                                      : '#333',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  fontSize: 12,
                                }}
                              >
                                {size}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                      {sport.categories?.enabled && (
                        <Box mb={1}>
                          <Typography
                            variant="caption"
                            style={{ fontWeight: 600 }}
                          >
                            {sport.categories.multiple
                              ? `Categoria${
                                  sport.categories.required ? ' *' : ''
                                } (selecione até ${
                                  sport.categories.maxSelection || 2
                                })`
                              : `Categoria${
                                  sport.categories.required ? ' *' : ''
                                }`}
                          </Typography>
                          {sport.categories.multiple ? (
                            (() => {
                              const currentCats = Array.isArray(
                                modalities[keys.categories]
                              )
                                ? modalities[keys.categories]
                                : [];
                              const maxSelection =
                                sport.categories.maxSelection || 2;
                              const limitReached =
                                currentCats.length >= maxSelection;
                              return (
                                <FormGroup>
                                  {sport.categories.options.map((cat) => {
                                    const isChecked = currentCats.includes(cat);
                                    const isDisabled =
                                      !isChecked && limitReached;
                                    return (
                                      <FormControlLabel
                                        key={cat}
                                        disabled={isDisabled}
                                        control={
                                          <Checkbox
                                            checked={isChecked}
                                            disabled={isDisabled}
                                            onChange={() => {
                                              if (isChecked) {
                                                updateModality(
                                                  keys.categories,
                                                  currentCats.filter(
                                                    (c) => c !== cat
                                                  )
                                                );
                                                if (
                                                  isPartnerPerCategory(sport)
                                                ) {
                                                  const partnerValue =
                                                    modalities[keys.partner] &&
                                                    typeof modalities[
                                                      keys.partner
                                                    ] === 'object'
                                                      ? {
                                                          ...modalities[
                                                            keys.partner
                                                          ],
                                                        }
                                                      : {};
                                                  delete partnerValue[cat];
                                                  updateModality(
                                                    keys.partner,
                                                    partnerValue
                                                  );
                                                }
                                              } else {
                                                if (limitReached) return;
                                                updateModality(
                                                  keys.categories,
                                                  [...currentCats, cat]
                                                );
                                              }
                                            }}
                                            color="primary"
                                            size="small"
                                          />
                                        }
                                        label={
                                          <Typography variant="caption">
                                            {cat}
                                          </Typography>
                                        }
                                      />
                                    );
                                  })}
                                </FormGroup>
                              );
                            })()
                          ) : (
                            <RadioGroup
                              value={modalities[keys.categories] || ''}
                              onChange={(e) =>
                                updateModality(keys.categories, e.target.value)
                              }
                            >
                              {sport.categories.options.map((cat) => (
                                <FormControlLabel
                                  key={cat}
                                  value={cat}
                                  control={
                                    <Radio color="primary" size="small" />
                                  }
                                  label={
                                    <Typography variant="caption">
                                      {cat}
                                    </Typography>
                                  }
                                />
                              ))}
                            </RadioGroup>
                          )}
                        </Box>
                      )}
                      {sport.partner_name?.enabled &&
                        isPartnerPerCategory(sport) &&
                        (() => {
                          const selectedCategories = Array.isArray(
                            modalities[keys.categories]
                          )
                            ? modalities[keys.categories]
                            : [];
                          if (selectedCategories.length === 0) return null;
                          const partnerValue =
                            modalities[keys.partner] &&
                            typeof modalities[keys.partner] === 'object'
                              ? modalities[keys.partner]
                              : {};
                          const requiredMark = sport.partner_name.required
                            ? ' *'
                            : '';
                          return (
                            <Box mb={1}>
                              {selectedCategories.map((category, idx) => (
                                <TextField
                                  key={category}
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  label={`${sport.partner_name.label} - ${category}${requiredMark}`}
                                  value={partnerValue[category] || ''}
                                  onChange={(e) =>
                                    updateModality(keys.partner, {
                                      ...partnerValue,
                                      [category]: e.target.value,
                                    })
                                  }
                                  style={{
                                    marginTop: idx === 0 ? 0 : 6,
                                    marginBottom: 4,
                                  }}
                                  InputProps={{ style: { fontSize: 13 } }}
                                />
                              ))}
                            </Box>
                          );
                        })()}
                      {sport.partner_name?.enabled &&
                        !isPartnerPerCategory(sport) && (
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            label={sport.partner_name.label}
                            value={
                              typeof modalities[keys.partner] === 'string'
                                ? modalities[keys.partner]
                                : ''
                            }
                            onChange={(e) =>
                              updateModality(keys.partner, e.target.value)
                            }
                            style={{ marginBottom: 4 }}
                            InputProps={{ style: { fontSize: 13 } }}
                          />
                        )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Collapse>
        </Box>
      )}

      {/* Sarau Cultural do acompanhante */}
      {sarauOptions.length > 0 && (
        <Box style={{ padding: '0 32px 8px 32px' }}>
          <Divider style={{ marginBottom: 4 }} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            onClick={() => setSarauOpen(!sarauOpen)}
            style={{ cursor: 'pointer', padding: '8px 0' }}
          >
            <Box>
              <Typography
                style={{ fontWeight: 700, fontSize: '1rem', color: '#1a3a5c' }}
              >
                Sarau Cultural
              </Typography>
              <Typography
                variant="body2"
                style={{ color: '#666', fontSize: '0.8rem' }}
              >
                Selecione até {sarauLimit} atividades
              </Typography>
            </Box>
            <ExpandMoreIcon
              style={{
                color: '#1a3a5c',
                transform: sarauOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
          <Collapse in={sarauOpen}>
            {sarauOptions.map((option) => {
              const keys = getSarauFieldKeys(option);
              const checked = !!sarauActivities[option.id];
              const sarauCount = countSelectedSarau(
                sarauActivities,
                sarauOptions
              );
              const disabled =
                !checked &&
                option.special_type !== 'opt_out' &&
                sarauCount >= sarauLimit;
              return (
                <Box key={option.id} style={{ marginBottom: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() => handleSarauToggle(option)}
                        disabled={disabled}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        style={{ fontWeight: checked ? 600 : 400 }}
                      >
                        {option.name}
                      </Typography>
                    }
                  />
                  {option.extra_text?.enabled && (
                    <Collapse in={checked}>
                      <Box ml={4} mb={1}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          label={option.extra_text.label}
                          value={sarauActivities[keys.details] || ''}
                          onChange={(e) =>
                            updateSarau(keys.details, e.target.value)
                          }
                          InputProps={{ style: { fontSize: 13 } }}
                        />
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
            {/* Opção de não participar do Sarau */}
            {(() => {
              const optOut = sarauOptions.find(
                (s) => s.special_type === 'opt_out'
              ) || {
                id: 'nao_participar_sarau',
                name: 'Não irei participar do Sarau',
                special_type: 'opt_out',
              };
              const isOptOutInList = sarauOptions.some(
                (s) => s.special_type === 'opt_out'
              );
              if (isOptOutInList) return null;
              const checked = !!sarauActivities[optOut.id];
              return (
                <Box style={{ marginBottom: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() => handleSarauToggle(optOut)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        style={{ fontWeight: checked ? 600 : 400 }}
                      >
                        {optOut.name}
                      </Typography>
                    }
                  />
                </Box>
              );
            })()}
          </Collapse>
        </Box>
      )}

      {/* Termos de aceite do acompanhante */}
      <Box style={{ padding: '0 32px 8px 32px' }}>
        <Divider style={{ marginBottom: 12 }} />
        <Typography
          style={{
            fontWeight: 700,
            fontSize: '1rem',
            color: '#1a3a5c',
            marginBottom: 8,
          }}
        >
          Termos e Autorizações
        </Typography>
        {COMPANION_TERMS.map((term) => {
          const checked = !!terms[term.field];
          return (
            <Box
              key={term.field}
              style={{
                border: checked ? '2px solid #4caf50' : '1px solid #e0e0e0',
                backgroundColor: checked ? '#f1f8e9' : 'transparent',
                borderRadius: 8,
                padding: '6px 12px',
                marginBottom: 8,
                transition: 'all 0.2s',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    onChange={() =>
                      setTerms((prev) => ({
                        ...prev,
                        [term.field]: !prev[term.field],
                      }))
                    }
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {term.label}
                    {term.required && (
                      <span style={{ color: '#d32f2f' }}> *</span>
                    )}
                  </Typography>
                }
              />
            </Box>
          );
        })}
      </Box>

      {(availableSports.length > 0 && !hasSportSelection) ||
      (availableSarau.length > 0 && !hasSarauSelection) ? (
        <Box style={{ padding: '0 32px' }}>
          <Typography
            variant="body2"
            style={{
              color: '#cc6600',
              backgroundColor: '#fff5ee',
              border: '1px solid #ffe0cc',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 13,
            }}
          >
            {availableSports.length > 0 && !hasSportSelection
              ? 'Selecione ao menos uma modalidade esportiva (ou "Não irei participar").'
              : null}
            {availableSports.length > 0 &&
            !hasSportSelection &&
            availableSarau.length > 0 &&
            !hasSarauSelection ? (
              <br />
            ) : null}
            {availableSarau.length > 0 && !hasSarauSelection
              ? 'Selecione ao menos uma atividade do Sarau (ou "Não irei participar").'
              : null}
          </Typography>
        </Box>
      ) : null}

      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ padding: '12px 32px 28px 32px', gap: 12 }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          style={{
            textTransform: 'none',
            borderRadius: 8,
            fontWeight: 600,
            borderColor: '#ccc',
            color: '#333',
            padding: '8px 24px',
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!canSave}
          style={{
            backgroundColor: canSave ? '#1a3a5c' : '#ccc',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
            padding: '8px 24px',
            boxShadow: 'none',
          }}
        >
          Adicionar Acompanhante
        </Button>
      </Box>
    </Dialog>
  );
}

export default function StepHospedagem({
  formData,
  updateFormData,
  activityConfig,
  calculateTotal,
  availability,
}) {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);

  const goingAlone = formData.going_alone;
  const companions = formData.companions || [];
  const activeRoomRules =
    resolveActiveRoomRules(activityConfig, formData.hotel_id) ||
    activityConfig?.roomRules ||
    {};
  const assignedCompanions = assignCompanionsToRooms(
    companions,
    activeRoomRules
  );

  React.useEffect(() => {
    if (companions.length !== assignedCompanions.length) return;

    const hasRoomDrift = companions.some((companion, index) => {
      const assigned = assignedCompanions[index];
      if (!assigned) return false;
      return (
        companion.room_number !== assigned.room_number ||
        companion.is_minor !== assigned.is_minor ||
        companion.is_paying !== assigned.is_paying
      );
    });

    if (hasRoomDrift) {
      updateFormData('companions', assignedCompanions);
    }
  }, [assignedCompanions, companions, updateFormData]);

  const toggleAlone = () => {
    if (!goingAlone) {
      updateFormData('companions', []);
    }
    updateFormData('going_alone', !goingAlone);
  };

  const openAddModal = () => {
    setEditingIndex(-1);
    setModalOpen(true);
  };

  const handleSaveCompanion = (companion) => {
    const updated = [...companions];
    if (editingIndex >= 0) {
      updated[editingIndex] = companion;
    } else {
      updated.push(companion);
    }
    updateFormData(
      'companions',
      assignCompanionsToRooms(updated, activeRoomRules)
    );
    setModalOpen(false);
  };

  const removeCompanion = (index) => {
    const updated = companions.filter((_, i) => i !== index);
    updateFormData(
      'companions',
      assignCompanionsToRooms(updated, activeRoomRules)
    );
  };

  const totals = calculateTotal();
  const requestedRooms = 1 + (totals.extraRooms || 0);

  const availableHotels = availability?.hotels || [];
  const allHotelsSoldOut =
    availableHotels.length > 0 &&
    availableHotels.every((hotel) => !isHotelSelectable(hotel));

  const handleHotelSelect = (hotel) => {
    if (!isHotelSelectable(hotel)) return;
    updateFormData('hotel_id', hotel.id);
  };

  const renderHotelSelection = () => {
    if (availableHotels.length === 0) return null;

    return (
      <Box mb={2}>
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 700, marginBottom: 8, color: '#1a3a5c' }}
        >
          Escolha o hotel
        </Typography>
        <Box className={classes.hotelGrid}>
          {availableHotels.map((hotel) => {
            const configuredHotel =
              findHotelInActivity(activityConfig, hotel.id) || hotel;
            const hotelRoomRules = configuredHotel.roomRules || {};
            const companionPrice = Number(hotelRoomRules.companionPrice || 0);
            const additionalRoomPriceLabel =
              formatAdditionalRoomPriceLabel(hotelRoomRules);
            const isSelected = formData.hotel_id === hotel.id;
            const inactive = hotel.active === false;
            const soldOut =
              hotel.isLimited &&
              typeof hotel.remaining === 'number' &&
              hotel.remaining <= 0;
            const disabled = inactive || soldOut;
            const cardClass = `${classes.hotelCard} ${
              isSelected && !disabled ? classes.hotelCardSelected : ''
            } ${disabled ? classes.hotelCardDisabled : ''}`;

            return (
              <Box
                key={hotel.id}
                className={cardClass}
                onClick={() => handleHotelSelect(hotel)}
                aria-disabled={disabled}
              >
                {soldOut && (
                  <Box
                    className={classes.hotelBadge}
                    style={{ backgroundColor: '#ffebee', color: '#c62828' }}
                  >
                    Esgotado
                  </Box>
                )}
                {inactive && !soldOut && (
                  <Box
                    className={classes.hotelBadge}
                    style={{ backgroundColor: '#eeeeee', color: '#616161' }}
                  >
                    Indisponível
                  </Box>
                )}
                <Typography
                  variant="body1"
                  style={{ fontWeight: 700, color: '#1a3a5c', marginRight: 64 }}
                >
                  {hotel.name}
                </Typography>
                {hotel.description && (
                  <Typography
                    variant="caption"
                    style={{ color: '#666', display: 'block', marginTop: 4 }}
                  >
                    {hotel.description}
                  </Typography>
                )}
                <Box mt={1}>
                  {companionPrice > 0 ? (
                    <Typography
                      variant="caption"
                      style={{ display: 'block', color: '#1a3a5c' }}
                    >
                      Acompanhante: R$ {formatMoney(companionPrice)}
                    </Typography>
                  ) : null}
                  {additionalRoomPriceLabel ? (
                    <Typography
                      variant="caption"
                      style={{ display: 'block', color: '#1a3a5c' }}
                    >
                      Quarto adicional: {additionalRoomPriceLabel}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Box>
        {allHotelsSoldOut && (
          <Box
            style={{
              backgroundColor: '#ffebee',
              border: '1px solid #e57373',
              color: '#c62828',
              borderRadius: 8,
              padding: 12,
              fontSize: 13,
            }}
          >
            <strong>Hospedagem esgotada em todos os hotéis.</strong> Não há
            hotéis com quartos disponíveis no momento.
          </Box>
        )}
      </Box>
    );
  };

  const renderAvailabilityBanners = () => {
    if (!availability) return null;
    if (availableHotels.length > 0) return null; // banners agora são por-hotel
    const banners = [];
    const baseStyle = {
      borderRadius: 8,
      padding: 12,
      marginBottom: 12,
      fontSize: 13,
    };

    if (
      availability.isLimited &&
      typeof availability.remaining === 'number' &&
      availability.remaining <= 0
    ) {
      banners.push(
        <Box
          key="sold-out"
          style={{
            ...baseStyle,
            backgroundColor: '#ffebee',
            border: '1px solid #e57373',
            color: '#c62828',
          }}
        >
          <strong>Hospedagem esgotada.</strong> Todos os quartos deste evento já
          foram reservados. Você ainda pode se inscrever sem hospedagem.
        </Box>
      );
    }

    const maxPerReg = availability.max_rooms_per_registration;
    if (maxPerReg && maxPerReg > 0) {
      const overLimit = requestedRooms > maxPerReg;
      banners.push(
        <Box
          key="max-per-reg"
          style={{
            ...baseStyle,
            backgroundColor: overLimit ? '#ffebee' : '#e3f2fd',
            border: `1px solid ${overLimit ? '#e57373' : '#64b5f6'}`,
            color: overLimit ? '#c62828' : '#0d47a1',
          }}
        >
          {overLimit ? (
            <>
              <strong>Limite excedido:</strong> você está solicitando{' '}
              {requestedRooms} quarto(s), mas o máximo por inscrição é{' '}
              {maxPerReg}.
            </>
          ) : (
            <>
              Máximo de {maxPerReg} quarto(s) por inscrição. Atualmente você
              solicitou {requestedRooms}.
            </>
          )}
        </Box>
      );
    }

    if (
      availability.isLimited &&
      typeof availability.remaining === 'number' &&
      availability.remaining > 0 &&
      requestedRooms > availability.remaining
    ) {
      banners.push(
        <Box
          key="over-remaining"
          style={{
            ...baseStyle,
            backgroundColor: '#ffebee',
            border: '1px solid #e57373',
            color: '#c62828',
          }}
        >
          <strong>Quartos insuficientes:</strong> não há quartos disponíveis
          para a quantidade solicitada.
        </Box>
      );
    }

    return banners.length > 0 ? <Box>{banners}</Box> : null;
  };

  // Agrupar acompanhantes por quarto
  const roomGroups = {};
  assignedCompanions.forEach((c, i) => {
    const room = c.room_number || 1;
    if (!roomGroups[room]) roomGroups[room] = [];
    roomGroups[room].push({ ...c, _index: i });
  });

  return (
    <Box>
      <Typography variant="h5" className={classes.title}>
        Acompanhantes e Hospedagem
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Configure os acompanhantes e a distribuição de quartos
      </Typography>

      {renderAvailabilityBanners()}

      {renderHotelSelection()}

      <Box
        className={`${classes.aloneCard} ${
          goingAlone ? classes.aloneCardSelected : ''
        }`}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={goingAlone}
              onChange={toggleAlone}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                Irei sozinho(a), sem acompanhantes
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Sem custo adicional de hospedagem
              </Typography>
            </Box>
          }
        />
      </Box>

      {goingAlone && (
        <Box mb={2}>
          <Typography
            variant="body2"
            style={{ fontWeight: 500, marginBottom: 4 }}
          >
            Indicação para dividir quarto (opcional)
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Nome de alguém que gostaria de dividir o quarto"
            value={formData.room_share_name}
            onChange={(e) => updateFormData('room_share_name', e.target.value)}
          />
        </Box>
      )}

      {!goingAlone && (
        <>
          <Box className={classes.sectionHeader}>
            <Box display="flex" alignItems="center">
              <PersonOutlineIcon style={{ marginRight: 8 }} />
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                Gerenciamento de Acompanhantes
              </Typography>
            </Box>
            <Button
              className={classes.btnAdd}
              startIcon={<AddIcon />}
              onClick={openAddModal}
              size="small"
            >
              Adicionar
            </Button>
          </Box>

          {assignedCompanions.length === 0 ? (
            <Box className={classes.emptyState}>
              <PersonOutlineIcon style={{ fontSize: 48, color: '#bbb' }} />
              <Typography variant="body1" color="textSecondary">
                Nenhum acompanhante adicionado
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Clique em "Adicionar" para incluir acompanhantes
              </Typography>
            </Box>
          ) : (
            Object.keys(roomGroups).map((roomNum) => (
              <Box key={roomNum} mb={2}>
                <Typography
                  variant="subtitle2"
                  style={{ fontWeight: 'bold', marginBottom: 4 }}
                >
                  Quarto {roomNum}
                </Typography>
                {roomGroups[roomNum].map((c) => {
                  const cSportCount = activityConfig?.sports
                    ? countSelectedSports(
                        c.modalities || {},
                        activityConfig.sports
                      )
                    : 0;
                  const cSarauCount = activityConfig?.sarau
                    ? countSelectedSarau(
                        c.sarau_activities || {},
                        activityConfig.sarau
                      )
                    : 0;
                  const paymentStatus = getCompanionPaymentStatus(
                    c,
                    activeRoomRules
                  );
                  return (
                    <Box key={c._index} className={classes.companionCard}>
                      <Box flex={1}>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {c.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          CPF: {c.cpf} {paymentStatus.label}
                        </Typography>
                        {(cSportCount > 0 || cSarauCount > 0) && (
                          <Typography
                            variant="caption"
                            style={{
                              display: 'block',
                              color: '#1a3a5c',
                              marginTop: 2,
                            }}
                          >
                            {cSportCount > 0 ? `${cSportCount} esporte(s)` : ''}
                            {cSportCount > 0 && cSarauCount > 0 ? ' | ' : ''}
                            {cSarauCount > 0 ? `${cSarauCount} sarau` : ''}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingIndex(c._index);
                            setModalOpen(true);
                          }}
                        >
                          <EditIcon
                            fontSize="small"
                            style={{ color: '#1a3a5c' }}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => removeCompanion(c._index)}
                        >
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))
          )}

          {assignedCompanions.length > 0 && (
            <Box className={classes.valuesBox}>
              <Typography
                variant="subtitle1"
                style={{
                  fontWeight: 'bold',
                  textAlign: 'center',
                  marginBottom: 8,
                }}
              >
                Resumo de Valores
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">
                  {totals.payingCompanions} Acompanhante(s) pagante(s)
                </Typography>
                <Typography variant="body2">
                  R$ {totals.companionTotal.toFixed(2).replace('.', ',')}
                </Typography>
              </Box>
              {totals.freeCompanions > 0 && (
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">
                    {totals.freeCompanions} Acompanhante(s) não pagante(s)
                  </Typography>
                  <Typography variant="body2">R$ 0,00</Typography>
                </Box>
              )}
              {totals.extraRooms > 0 && (
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" style={{ flex: 1, paddingRight: 8 }}>
                    {formatExtraRoomsBillingLabel(totals)}
                  </Typography>
                  <Typography variant="body2" style={{ flexShrink: 0 }}>
                    R$ {totals.roomTotal.toFixed(2).replace('.', ',')}
                  </Typography>
                </Box>
              )}
              <Divider style={{ margin: '8px 0' }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                  Total
                </Typography>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                  R$ {totals.total.toFixed(2).replace('.', ',')}
                </Typography>
              </Box>
            </Box>
          )}
        </>
      )}

      <CompanionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveCompanion}
        initialData={
          editingIndex >= 0 ? assignedCompanions[editingIndex] : EMPTY_COMPANION
        }
        roomRules={activeRoomRules}
        activityConfig={activityConfig}
      />
    </Box>
  );
}
